// Using native fetch API as requested
import { Capacitor, CapacitorHttp } from '@capacitor/core';

// Helper function to convert affiliate IDs to display names
const getAffiliateDisplayName = (affiliateId: string): string => {
  const affiliateMap: { [key: string]: string } = {
    'zen_wellness': 'Zen Wellness',
    'mindful_company': 'Mindful Company',
    'internal': 'Internal',
    'mindful20': 'Mindful Company',
    'welcome10': 'Internal'
  };
  return affiliateMap[affiliateId] || affiliateId.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export interface ReferralCode {
  code: string;
  affiliateId: string;
  affiliateName: string;
  maxUses: number;
  currentUses: number;
  validFrom: string;
  validUntil: string;
  description?: string;
  isActive: boolean;
}

export interface ReferralCodeUsage {
  code: string;
  
  usedAt: string;
  affiliateId: string;
  affiliateName: string;
  referredBy: string;
}

export interface ReferralCodeValidation {
  isValid: boolean;
  code?: string;
  affiliateId?: string;
  affiliateName?: string;
  message: string;
  maxUses?: number;
  currentUses?: number;
}

export interface ReferralAnalytics {
  totalReferrals: number;
  uniqueAffiliates: number;
  topAffiliates: Array<{
    affiliateId: string;
    affiliateName: string;
    referralCount: number;
    conversionRate: number;
  }>;
  recentReferrals: ReferralCodeUsage[];
}

const isNative = Capacitor.isNativePlatform();

const nativeGetJson = async (url: string, headers?: Record<string, string>) => {
  return CapacitorHttp.get({
    url,
    headers
  });
};

const nativePostJson = async (
  url: string,
  data: Record<string, unknown>,
  headers?: Record<string, string>
) => {
  return CapacitorHttp.post({
    url,
    headers,
    data
  });
};

// Check if referral code exists (no auth needed)
export const checkReferralCode = async (code: string): Promise<ReferralCodeValidation> => {
  const apiUrl = `https://api.luminawellnessapp.com/referral/check/${code}`;
  
  console.log('🔍 Checking referral code at URL:', apiUrl);
  console.log('🔍 Code being checked:', code);
  
  try {
    let data: any;

    if (isNative) {
      const response = await nativeGetJson(apiUrl, { 'Content-Type': 'application/json' });
      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', response.headers);

      if (response.status >= 400) {
        const errorText = JSON.stringify(response.data);
        console.error('❌ Response not OK:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      data = response.data;
    } else {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response not OK:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      data = await response.json();
    }
    console.log('✅ Referral code check successful:', data);
    console.log('🔍 Data type:', typeof data);
    console.log('🔍 Data keys:', Object.keys(data));
    console.log('🔍 Raw data:', JSON.stringify(data, null, 2));
    
    // Normalize the response to match expected format
    let normalizedData: ReferralCodeValidation = {
      isValid: false,
      message: 'Unknown response format'
    };
    
    // Handle different possible response formats
    if (data.isValid !== undefined) {
      // Standard format
      normalizedData = data;
    } else if (data.success !== undefined) {
      // Alternative format: success instead of isValid
      normalizedData = {
        isValid: Boolean(data.success),
        message: data.message || data.msg || 'Code validation result',
        code: data.code,
        affiliateId: data.affiliateId,
        affiliateName: data.affiliateName,
        maxUses: data.maxUses,
        currentUses: data.currentUses
      };
    } else if (data.data && typeof data.data === 'object') {
      // Nested format: data.isValid
      normalizedData = data.data;
    } else if (data.status === 'success' || data.status === 'valid') {
      // Status-based format
      normalizedData = {
        isValid: true,
        message: data.message || 'Code is valid',
        code: data.code,
        affiliateId: data.affiliateId,
        affiliateName: data.affiliateName,
        maxUses: data.maxUses,
        currentUses: data.currentUses
      };
         } else {
       // Unknown format, try to extract what we can
       console.warn('⚠️ Unknown response format, attempting to normalize');
       const isValid = Boolean(data.valid || data.validated || data.exists);
       normalizedData = {
         isValid: isValid,
         message: data.message || data.msg || data.description || (isValid ? 'Code validation completed' : 'Invalid referral code'),
         code: data.code,
         affiliateId: data.affiliateId,
         affiliateName: data.affiliateName,
         maxUses: data.maxUses,
         currentUses: data.currentUses
       };
     }
    
    console.log('🔍 Normalized data:', normalizedData);
    return normalizedData;
  } catch (error) {
    console.error('❌ Error checking referral code:', error);
    
    // More specific error messages
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        isValid: false,
        message: 'Cannot connect to server. Please check if your backend is running.',
      };
    }
    
    if (error instanceof Error && error.message.includes('CORS')) {
      return {
        isValid: false,
        message: 'CORS error. Please check your backend CORS configuration.',
      };
    }
    
    return {
      isValid: false,
      message: `Failed to check referral code: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
};

// Validate and track referral code usage (requires auth)
export const validateReferralCode = async (
  getIdToken: () => Promise<string | null>,
  code: string
): Promise<ReferralCodeValidation> => {
  try {
    console.log('🔐 Validating referral code with authentication...');
    console.log('🔐 Code to validate:', code);
    
    // Get the token first to debug
    console.log('🔐 About to call getIdToken() function...');
    const token = await getIdToken();
    console.log('🔐 Firebase ID token obtained:', token ? 'YES' : 'NO');
    
    // Check if user is authenticated
    if (!token) {
      console.warn('⚠️ No Firebase ID token available - user not authenticated');
      return {
        isValid: false,
        message: 'You must be signed in to validate referral codes. Please sign in and try again.'
      };
    }
    
    console.log('🔐 Token length:', token.length);
    console.log('🔐 Token preview:', token.substring(0, 20) + '...');
    console.log('🔐 Token type:', typeof token);
    
    // Use exact fetch format as requested
    const apiUrl = 'https://api.luminawellnessapp.com/referral/validate';
    
    console.log('🔐 Making authenticated POST request to /referral/validate');
    console.log('🔐 URL:', apiUrl);
    console.log('🔐 Headers being sent:', {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    });
    
     const requestBody = {
       code: code
     };
     console.log('🔐 Request body being sent:', requestBody);
     
     let data: any;

     if (isNative) {
       const response = await nativePostJson(apiUrl, requestBody, {
         'Content-Type': 'application/json',
         'Authorization': `Bearer ${token}` // Firebase ID token required by backend
       });

       if (response.status >= 400) {
         const errorText = JSON.stringify(response.data);
         console.error('❌ Response not OK:', response.status, errorText);
      
         // Handle specific HTTP status codes
         if (response.status === 422) {
           console.error('❌ 422 Unprocessable Entity - Request validation failed');
           console.error('❌ Error details:', errorText);
        
           // Try to parse the error response for more details
           try {
             const errorData = JSON.parse(errorText);
             if (errorData.detail && Array.isArray(errorData.detail)) {
               const validationErrors = errorData.detail.map((err: any) =>
                 `${err.loc?.join('.') || 'field'}: ${err.msg}`
               ).join(', ');
               throw new Error(`Validation failed: ${validationErrors}`);
             }
           } catch (parseError) {
             // If we can't parse the error, use the raw text
           }
        
           throw new Error(`Request validation failed (422): ${errorText}`);
         }

         throw new Error(`HTTP ${response.status}: ${errorText}`);
       }

       data = response.data;
     } else {
       const response = await fetch(apiUrl, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}` // Firebase ID token required by backend
         },
         body: JSON.stringify(requestBody)
       });

       if (!response.ok) {
         const errorText = await response.text();
         console.error('❌ Response not OK:', response.status, errorText);

         // Handle specific HTTP status codes
         if (response.status === 422) {
           console.error('❌ 422 Unprocessable Entity - Request validation failed');
           console.error('❌ Error details:', errorText);

           // Try to parse the error response for more details
           try {
             const errorData = JSON.parse(errorText);
             if (errorData.detail && Array.isArray(errorData.detail)) {
               const validationErrors = errorData.detail.map((err: any) =>
                 `${err.loc?.join('.') || 'field'}: ${err.msg}`
               ).join(', ');
               throw new Error(`Validation failed: ${validationErrors}`);
             }
           } catch (parseError) {
             // If we can't parse the error, use the raw text
           }

           throw new Error(`Request validation failed (422): ${errorText}`);
         }

         throw new Error(`HTTP ${response.status}: ${errorText}`);
       }

       data = await response.json();
     }
    console.log('✅ Referral code validation successful:', data);
    console.log('🔍 Response data type:', typeof data);
    console.log('🔍 Response data keys:', Object.keys(data || {}));
    console.log('🔍 Response data message field:', data?.message);
    console.log('🔍 Response data isValid field:', data?.isValid);
    console.log('🔍 Full response data:', JSON.stringify(data, null, 2));
    
    // Normalize the response to match expected format
    let normalizedData: ReferralCodeValidation = {
      isValid: false,
      message: 'Unknown response format'
    };
    
         // Handle different possible response formats
     if (data.message && data.isValid !== undefined) {
       // Standard format
       normalizedData = data;
     } else if (data.valid !== undefined && data.error !== undefined) {
       // Your backend format: valid + error
       normalizedData = {
         isValid: Boolean(data.valid),
         message: data.error || 'Referral code validation completed',
         code: data.code,
         affiliateId: data.affiliateId,
         affiliateName: data.affiliateName
       };
     } else if (data.text && data.status === 'success') {
       // Alternative format: text + status
       normalizedData = {
         isValid: true,
         message: data.text,
         code: data.code,
         affiliateId: data.affiliateId,
         affiliateName: data.affiliateName
       };
     } else if (data.success && data.message) {
       // Success-based format
       normalizedData = {
         isValid: Boolean(data.success),
         message: data.message,
         code: data.code,
         affiliateId: data.affiliateId,
         affiliateName: data.affiliateName
       };
     } else if (data.data && typeof data.data === 'object') {
       // Nested format
       normalizedData = {
         isValid: true,
         message: data.data.message || 'Referral code applied successfully',
         code: data.data.code,
         affiliateId: data.data.affiliateId,
         affiliateName: data.data.affiliateName
       };
           } else {
        // Unknown format, try to extract what we can
        console.warn('⚠️ Unknown response format, attempting to normalize');
        const isValid = Boolean(data.success || data.status === 'success' || data.valid);
        normalizedData = {
          isValid: isValid,
          message: data.message || data.text || data.description || data.error || (isValid ? 'Referral code processed' : 'Invalid referral code'),
          code: data.code,
          affiliateId: data.affiliateId,
          affiliateName: data.affiliateName
        };
      }
    
    console.log('🔍 Normalized data:', normalizedData);
    return normalizedData;
  } catch (error: any) {
    console.error('❌ Error validating referral code:', error);
    console.error('❌ Error response status:', error.response?.status);
    console.error('❌ Error response data:', error.response?.data);
    console.error('❌ Error message:', error.message);
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      if (error.response?.data?.message?.includes('token') || error.response?.data?.message?.includes('expired')) {
        return {
          isValid: false,
          message: 'Your session has expired. Please sign out and sign back in to continue.'
        };
      }
      return {
        isValid: false,
        message: 'Authentication failed. Please make sure you are signed in and try again.'
      };
    }
    
    if (error.response?.status === 403) {
      return {
        isValid: false,
        message: 'Access denied. You do not have permission to validate referral codes.'
      };
    }
    
    return {
      isValid: false,
      message: error.response?.data?.message || 'Failed to validate referral code. Please try again.',
    };
  }
};

// Get user's referral code history (requires auth)
export const getUserReferralHistory = async (
  getIdToken: () => Promise<string | null>
): Promise<ReferralCodeUsage[]> => {
  const requestHistory = async (token: string) => {
    const apiUrl = 'https://api.luminawellnessapp.com/referral/user-history';

    if (isNative) {
      const response = await nativeGetJson(apiUrl, {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'authorization': `Bearer ${token}`
      });

      return {
        ok: response.status < 400,
        status: response.status,
        data: response.data
      };
    }

    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    let data: any = null;
    try {
      data = await response.json();
    } catch (error) {
      data = null;
    }

    return {
      ok: response.ok,
      status: response.status,
      data
    };
  };

  try {
    console.log('getUserReferralHistory: Starting...');
    let token = await getIdToken();
    if (!token) {
      console.warn('No token available for referral history request');
      return [];
    }

    let response = await requestHistory(token);
    if (!response.ok && response.status === 401) {
      console.warn('getUserReferralHistory: 401 received, retrying with refreshed token');
      token = await getIdToken();
      if (token) {
        response = await requestHistory(token);
      }
    }

    if (!response.ok) {
      const errorText = JSON.stringify(response.data);
      console.error('getUserReferralHistory: Response not OK:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = response.data;
    if (Array.isArray(data)) {
      return data;
    }

    if (data && Array.isArray(data.used_codes)) {
      const uniqueCodes = new Set();
      const normalizedCodes: ReferralCodeUsage[] = [];
      data.used_codes.forEach((codeData: any) => {
        let actualCode = codeData.code || codeData.referral_code || codeData.code_id;
        if (!actualCode) {
          if (typeof codeData === 'string') {
            actualCode = codeData;
          } else {
            const allValues = Object.values(codeData);
            const stringValues = allValues.filter(val => typeof val === 'string');
            const knownCodes = ['WELCOME10', 'MINDFUL20', 'ZEN15'];
            actualCode = stringValues.find(val => knownCodes.includes(val as string)) as string;
            if (!actualCode && stringValues.length > 0) {
              actualCode = stringValues[0] as string;
            }
          }
        }
        if (actualCode && !uniqueCodes.has(actualCode)) {
          uniqueCodes.add(actualCode);
          normalizedCodes.push({
            code: actualCode,
            usedAt: new Date().toISOString(),
            affiliateId: data.affiliate || 'zen_wellness',
            affiliateName: getAffiliateDisplayName(data.affiliate || 'zen_wellness'),
            referredBy: data.affiliate || 'zen_wellness'
          });
        }
      });
      return normalizedCodes;
    }

    return [];
  } catch (error: any) {
    console.error('getUserReferralHistory: Error caught:', error);
    return [];
  }
};
// Get referral analytics (admin only)
export const getReferralAnalytics = async (
  getIdToken: () => Promise<string | null>
): Promise<ReferralAnalytics> => {
  try {
    const token = await getIdToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    const apiUrl = 'https://api.luminawellnessapp.com/referral/analytics';
    
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` // Firebase ID token required by backend
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Response not OK:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error: any) {
    console.error('Error getting referral analytics:', error);
    throw error;
  }
};

// Get current user's active referral code (if any)
export const getCurrentUserReferralCode = async (
  getIdToken: () => Promise<string | null>
): Promise<ReferralCodeUsage | null> => {
  try {
    const history = await getUserReferralHistory(getIdToken);
    // Return the most recent referral code used
    return history.length > 0 ? history[0] : null;
  } catch (error) {
    console.error('Error getting current user referral code:', error);
    return null;
  }
};

